<template>
  <div class="publishing-upload-item">
    <h4>{{ file.name }}</h4>
    <LoadingBar :execute-fn="uploadFile" />
    <div v-if="!hasError" class="upload-steps">
      <p :class="uploadStepState">
        <Icon :icon="uploadIcon" />
        {{ $t('PUBLISHING.UPLOAD.STAGE.UPLOADING') }}
      </p>
      <p :class="processingStepState">
        <Icon :icon="processingIcon" />
        {{ $t('PUBLISHING.UPLOAD.STAGE.PROCESSING') }}
      </p>
    </div>

    <div v-else class="upload-steps">
      <p class="FAILED">
        <Icon icon="exclamation" />{{ $t(errorMessage) }}
        <SmartFaqLink :link="faqLink" hide-icon />
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import publishingCoreService from 'commons/api/publishingCoreService/publishingCoreService';
import designUploadServiceV2 from 'commons/api/designUploadService/designUploadServiceV2';
import LoadingBar from 'src/app/components/loadingBar/LoadingBar.vue';
import { delay } from 'commons/utils';
import { usePublishingStore } from 'commons/stores/publishing';
import smartFaqLinks from 'commons/smartFaq/smartFaqLinks';
import SmartFaqLink from 'commons/helpLink/SmartFaqLink.vue';

const UPLOAD_STEPS_STATES = {
  WAITING: 'WAITING',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
};

const REJECTION_CODE_TO_MESSAGE = [
  {
    codes: ['TOO_MANY_PIXELS'],
    message: 'PUBLISHING.UPLOAD.ERROR.TOO_BIG',
  },
  {
    codes: ['SIDE_TOO_SHORT', 'TOO_FEW_PIXELS'],
    message: 'PUBLISHING.UPLOAD.ERROR.TOO_SMALL',
  },
];

export default {
  name: 'PublishingUploadItem',
  components: {
    LoadingBar,
    SmartFaqLink,
  },
  props: {
    file: { type: File, required: true },
  },
  emits: ['onDone'],
  data() {
    return {
      UPLOAD_STEPS_STATES,
      uploadStepState: UPLOAD_STEPS_STATES.WAITING,
      processingStepState: UPLOAD_STEPS_STATES.WAITING,
      errorMessage: null,
      faqLink: smartFaqLinks.DESIGN_UPLOAD,
    };
  },
  computed: {
    ...mapState(usePublishingStore, ['currentScope']),
    uploadIcon() {
      if (this.uploadStepState === UPLOAD_STEPS_STATES.SUCCEEDED) {
        return 'accept';
      } else if (this.uploadStepState === UPLOAD_STEPS_STATES.FAILED) {
        return 'close';
      } else if (this.uploadStepState === UPLOAD_STEPS_STATES.IN_PROGRESS) {
        return 'gear';
      } else {
        return 'hourglass';
      }
    },
    processingIcon() {
      if (this.processingStepState === UPLOAD_STEPS_STATES.SUCCEEDED) {
        return 'accept';
      } else if (this.processingStepState === UPLOAD_STEPS_STATES.FAILED) {
        return 'close';
      } else if (this.processingStepState === UPLOAD_STEPS_STATES.IN_PROGRESS) {
        return 'gear';
      } else {
        return 'hourglass';
      }
    },
    hasError() {
      return (
        this.uploadStepState === UPLOAD_STEPS_STATES.FAILED ||
        this.processingStepState === UPLOAD_STEPS_STATES.FAILED
      );
    },
  },
  methods: {
    async uploadFile() {
      let uploadUrl;
      this.uploadStepState = UPLOAD_STEPS_STATES.IN_PROGRESS;
      try {
        uploadUrl = await designUploadServiceV2.uploadFile(this.file);
        this.uploadStepState = UPLOAD_STEPS_STATES.SUCCEEDED;
      } catch (error) {
        this.uploadStepState = UPLOAD_STEPS_STATES.FAILED;
        this.mapUploadError(error);
        return Promise.reject(error);
      }

      this.processingStepState = UPLOAD_STEPS_STATES.IN_PROGRESS;
      try {
        await publishingCoreService.createCompositionFromDesignUploadUrlWithScope(
          uploadUrl,
          this.currentScope,
        );
        this.processingStepState = UPLOAD_STEPS_STATES.SUCCEEDED;
      } catch (error) {
        this.processingStepState = UPLOAD_STEPS_STATES.FAILED;
        this.mapUploadError(error);
        return Promise.reject(error);
      }

      await this.finish();
    },
    async finish() {
      await delay(2000);
      this.$emit('onDone');
    },
    mapUploadError(error) {
      if (error?.status === 415) {
        this.errorMessage = 'PUBLISHING.UPLOAD.ERROR.UNSUPPORTED_MEDIA_TYPE';
      } else if (error?.status === 429) {
        this.errorMessage = 'DESIGNS.UPLOAD.LIST.VALIDATION.LIMIT_REACHED';
      } else if (Array.isArray(error?.rejectionCodes)) {
        const found = REJECTION_CODE_TO_MESSAGE.find((mapping) =>
          mapping.codes.some((code) => error.rejectionCodes.includes(code)),
        );
        if (found) {
          this.errorMessage = found.message;
          return;
        }
        this.errorMessage = 'PUBLISHING.UPLOAD.ERROR.DEFAULT';
      } else {
        this.errorMessage = 'PUBLISHING.UPLOAD.ERROR.DEFAULT';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.publishing-upload-item {
  background-color: $grey5;
  border-radius: $border-radius-medium;
  padding: 24px;

  h4 {
    margin: 0 0 16px 0;
  }

  .loading-bar {
    margin: 16px 0;
  }

  .upload-steps {
    margin: 16px 0 0 0;
    p {
      display: flex;
      align-items: center;
      margin: 0 0 16px 0;
      .icon {
        margin-right: 8px;
        width: 20px;
        height: 20px;
      }
      &.SUCCEEDED {
        color: $pa-color-green;
      }

      &.FAILED {
        color: $pa-color-red;
        gap: 4px;
      }

      &.WAITING {
        color: $grey45;
      }

      &.IN_PROGRESS {
        .icon {
          animation: rotate 4s linear infinite;
        }
      }

      &:last-child {
        margin: 0;
      }
    }
  }
}
</style>
